import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ClavePermiso, RespuestaSesion, UsuarioSesion } from '@copathy/shared';
import { api, fijarAccessToken } from './api';

type EstadoAuth = {
  usuario: UsuarioSesion | null;
  permisos: readonly ClavePermiso[];
  cargando: boolean;
};

type ContextoAuth = EstadoAuth & {
  entrar: (correo: string, contrasena: string) => Promise<UsuarioSesion>;
  salir: () => Promise<void>;
  refrescarSesion: () => Promise<void>;
};

const Auth = createContext<ContextoAuth | null>(null);

export function ProveedorAuth({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoAuth>({
    usuario: null,
    permisos: [],
    cargando: true,
  });

  const aplicar = useCallback((sesion: RespuestaSesion) => {
    fijarAccessToken(sesion.accessToken);
    setEstado({ usuario: sesion.usuario, permisos: sesion.permisos, cargando: false });
  }, []);

  /**
   * Al abrir la app se intenta renovar con la cookie del refresh. Si el
   * navegador la tiene, la sesión sobrevive a recargar la página sin haber
   * guardado nada en `localStorage`.
   */
  useEffect(() => {
    let cancelado = false;
    void (async () => {
      const renovado = await api.renovar();
      if (cancelado) return;
      if (!renovado) {
        setEstado({ usuario: null, permisos: [], cargando: false });
        return;
      }
      try {
        const yo = await api.get<{ usuario: UsuarioSesion; permisos: ClavePermiso[] }>(
          '/api/auth/yo',
        );
        if (!cancelado) {
          setEstado({ usuario: yo.usuario, permisos: yo.permisos, cargando: false });
        }
      } catch {
        if (!cancelado) setEstado({ usuario: null, permisos: [], cargando: false });
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  const entrar = useCallback(
    async (correo: string, contrasena: string) => {
      const sesion = await api.post<RespuestaSesion>('/api/auth/login', { correo, contrasena });
      aplicar(sesion);
      return sesion.usuario;
    },
    [aplicar],
  );

  /**
   * Vuelve a leer la sesión desde el servidor.
   *
   * ⚠️ Hace falta porque los permisos del usuario **pueden cambiar mientras está
   * dentro**: quien edita los permisos de su propio rol seguiría viendo el menú
   * y los botones de antes hasta cerrar sesión. La UI solo oculta —el servidor
   * revalida siempre— pero una interfaz que muestra lo que ya no se puede hacer
   * se lee como una aplicación rota.
   */
  const refrescarSesion = useCallback(async () => {
    try {
      const yo = await api.get<{ usuario: UsuarioSesion; permisos: ClavePermiso[] }>(
        '/api/auth/yo',
      );
      setEstado({ usuario: yo.usuario, permisos: yo.permisos, cargando: false });
    } catch {
      // Si falla, se deja la sesión como está: la siguiente petición real dirá
      // si de verdad se cayó.
    }
  }, []);

  const salir = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      // Cerrar sesión nunca falla de cara al usuario: si el servidor no
      // responde, igual se limpia lo de este lado.
      fijarAccessToken(null);
      setEstado({ usuario: null, permisos: [], cargando: false });
    }
  }, []);

  const valor = useMemo<ContextoAuth>(
    () => ({ ...estado, entrar, salir, refrescarSesion }),
    [estado, entrar, salir, refrescarSesion],
  );

  return <Auth.Provider value={valor}>{children}</Auth.Provider>;
}

function useAuth(): ContextoAuth {
  const ctx = useContext(Auth);
  if (!ctx) throw new Error('useAuth se usó fuera de <ProveedorAuth>');
  return ctx;
}

export const useMe = () => {
  const { usuario, cargando } = useAuth();
  return { usuario, cargando };
};

/** Lista completa de permisos. La usa el Shell para armar el menú sin llamar
 *  a `usePuede` dentro de un bucle. */
export const usePermisos = () => useAuth().permisos;

export const useLogin = () => useAuth().entrar;
export const useLogout = () => useAuth().salir;
export const useRefrescarSesion = () => useAuth().refrescarSesion;

/**
 * Mitad del RBAC doble, la de la interfaz.
 *
 * ⚠️ Esto **solo oculta**. No es control de acceso: quien quiera puede llamar a
 * la API directo. La validación de verdad está en `requierePermiso` del
 * servidor, y ahí es donde no se negocia.
 */
export function usePuede(clave: ClavePermiso): boolean {
  return useAuth().permisos.includes(clave);
}
